export type StorageDiagnostics = {
  supported: boolean;
  persisted: boolean | null;
  usageBytes: number | null;
  quotaBytes: number | null;
  remainingBytes: number | null;
  persistenceRequest: "not-requested" | "granted" | "denied" | "unavailable";
  error?: string;
};

export async function readStorageDiagnostics(): Promise<StorageDiagnostics> {
  const storage = typeof navigator === "undefined" ? undefined : navigator.storage;
  if (!storage) {
    return unavailable("StorageManager is unavailable in this browser context.");
  }

  try {
    const estimate = storage.estimate ? await storage.estimate() : undefined;
    const persisted = storage.persisted ? await storage.persisted() : null;
    return toDiagnostics(persisted, estimate?.usage, estimate?.quota, "not-requested");
  } catch (error) {
    return unavailable(error instanceof Error ? error.message : String(error));
  }
}

export async function requestPersistentStorage(): Promise<StorageDiagnostics> {
  const storage = typeof navigator === "undefined" ? undefined : navigator.storage;
  if (!storage?.persist) {
    return unavailable("Persistent storage requests are unavailable in this browser context.");
  }

  try {
    const granted = await storage.persist();
    const estimate = storage.estimate ? await storage.estimate() : undefined;
    return toDiagnostics(granted, estimate?.usage, estimate?.quota, granted ? "granted" : "denied");
  } catch (error) {
    return unavailable(error instanceof Error ? error.message : String(error));
  }
}

function toDiagnostics(
  persisted: boolean | null,
  usage: number | undefined,
  quota: number | undefined,
  persistenceRequest: StorageDiagnostics["persistenceRequest"],
): StorageDiagnostics {
  const usageBytes = typeof usage === "number" ? usage : null;
  const quotaBytes = typeof quota === "number" ? quota : null;
  return {
    supported: true,
    persisted,
    usageBytes,
    quotaBytes,
    remainingBytes: usageBytes !== null && quotaBytes !== null ? Math.max(0, quotaBytes - usageBytes) : null,
    persistenceRequest,
  };
}

function unavailable(error: string): StorageDiagnostics {
  return {
    supported: false,
    persisted: null,
    usageBytes: null,
    quotaBytes: null,
    remainingBytes: null,
    persistenceRequest: "unavailable",
    error,
  };
}
